/**
 * Category forecasting: "where will Groceries land this period?"
 * (docs/ACTION-SCHEDULING-DESIGN.md, "Category forecasting").
 *
 *   forecast = actual spend so far
 *            + committed action occurrences remaining this period
 *            + expected discretionary remainder for the days left
 *
 * The discretionary remainder blends two estimates of *what is still to
 * come*: this period's pace (spent ÷ days elapsed × days remaining) and
 * history's tail — what landed after this same point in previous periods.
 * Blending remainders, rather than whole-period totals, stops pace's
 * uniform-accrual assumption from inventing spend that never arrives late in
 * the period (salary fully received by mid-month, rent paid on the 1st) and
 * lets history keep expecting a late bill it knows about. The pace weight is
 * the square of fraction-of-period elapsed, so history dominates early
 * (day-3 pace is noise) and pace takes over late (day-25 pace is truth).
 *
 * That blend is the default ('auto') method, but the user can choose how the
 * current period's remainder is estimated (FORECAST_METHODS, persisted as the
 * `forecastMethod` preference):
 *   - 'auto'     — the pace/history blend described above. Automatic
 *     adjustments: lumpy categories (few transactions per period) damp the
 *     pace weight, since a couple of large transactions say little about
 *     daily rhythm; with 12+ months of history on a monthly cadence, the
 *     same month last year's tail replaces the recent median (December ≠
 *     July).
 *   - 'pace'     — current pace: this period's daily rate carried through
 *     the days remaining, ignoring history.
 *   - 'typical'  — typical month: the median of the last completed periods
 *     (one-off spikes don't move it), floored at what is already locked in
 *     (spent + committed). Falls back to 'pace' until a completed period
 *     exists.
 *
 * Whatever the method, the figure stays explainable — every term is returned
 * so the UI can show "spent $412 + $170 power due 18th + ~$104 at your usual
 * pace = $686".
 */

import { generateOccurrences } from './generateOccurrences';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// User-selectable remainder estimators for the current period, in display
// order. `value` is what the `forecastMethod` preference stores.
export const FORECAST_METHODS = [
  {
    value: 'auto',
    label: 'Smart blend',
    short: 'Blend',
    description: "Blends this month's pace with what usually lands in the rest of the month: history early, pace late.",
  },
  {
    value: 'pace',
    label: 'Current pace',
    short: 'Pace',
    description: "This month's daily rate carried through the days left.",
  },
  {
    value: 'typical',
    label: 'Typical month',
    short: 'Typical',
    description: 'The median of your recent completed months, so one-off spikes don’t move it.',
  },
];

export const DEFAULT_FORECAST_METHOD = 'auto';

const normalizeMethod = (method) =>
  FORECAST_METHODS.some((m) => m.value === method) ? method : DEFAULT_FORECAST_METHOD;

// Most recent completed periods considered for the history median.
const HISTORY_PERIODS = 6;
// Same-month-last-year kicks in once this many completed periods have data.
const SEASONAL_MIN_PERIODS = 12;
// A period averaging this few transactions is "lumpy": damp the pace weight.
const LUMPY_TXN_COUNT = 3;
const LUMPY_PACE_DAMPING = 0.5;

const toLocalDay = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const dayDiff = (a, b) => Math.round((a - b) / MS_PER_DAY);

const round2 = (n) => Math.round(n * 100) / 100;

const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const isCalendarMonth = (start, end) =>
  start.getDate() === 1 &&
  end.getMonth() === start.getMonth() &&
  end.getFullYear() === start.getFullYear() &&
  end.getDate() === new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();

// The last `count` periods before [start, end], oldest last. Calendar months
// step by month; anything else steps back by the period's own length.
const derivePreviousPeriods = (start, end, count) => {
  const periods = [];
  if (isCalendarMonth(start, end)) {
    for (let m = 1; m <= count; m++) {
      const s = new Date(start.getFullYear(), start.getMonth() - m, 1);
      periods.push({ start: s, end: new Date(s.getFullYear(), s.getMonth() + 1, 0) });
    }
  } else {
    const len = dayDiff(end, start) + 1;
    for (let m = 1; m <= count; m++) {
      periods.push({ start: addDays(start, -len * m), end: addDays(end, -len * m) });
    }
  }
  return periods;
};

/**
 * The budget's own period containing `today`, anchored on its schedule (the
 * same anchoring the balance projection uses), plus its previous periods for
 * history. Null when the budget has no current period to forecast
 * (non-recurring without an end date, or a schedule that hasn't started).
 */
export const budgetCurrentPeriod = (budget, today = new Date()) => {
  const now = toLocalDay(today);
  if (!budget) return null;

  if (!budget.isRecurring) {
    const start = toLocalDay(budget.startDate);
    const end = toLocalDay(budget.endDate);
    if (!start || !end || end < start) return null;
    return { start, end, previousPeriods: [] };
  }

  const scheduleStart = toLocalDay(budget.startDate);
  if (!scheduleStart || scheduleStart > now) return null;

  // History periods are about the *category's* past spending, which predates
  // the budget — a budget created today should still see last winter's bills.
  // Extend the anchor pattern backward in phase with the schedule (24 months
  // is a multiple of every month-based cadence; weekly steps by weeks).
  const extendedStart = new Date(scheduleStart);
  if ((budget.recurringPeriod || 'monthly') === 'weekly') {
    extendedStart.setDate(extendedStart.getDate() - 7 * 104);
  } else {
    extendedStart.setMonth(extendedStart.getMonth() - 24);
  }

  const anchors = generateOccurrences(
    { ...budget, startDate: extendedStart, endDate: undefined },
    addDays(now, -800),
    addDays(now, 400)
  );
  for (let i = 0; i < anchors.length - 1; i++) {
    const start = anchors[i];
    const end = addDays(anchors[i + 1], -1);
    if (now >= start && now <= end) {
      const previousPeriods = [];
      for (let p = i - 1; p >= 0 && previousPeriods.length < SEASONAL_MIN_PERIODS; p--) {
        previousPeriods.push({ start: anchors[p], end: addDays(anchors[p + 1], -1) });
      }
      return { start, end, previousPeriods };
    }
  }
  return null;
};

/**
 * Forecast a category's landing figure for one period.
 *
 * @param {Object} args
 * @param {string} args.categoryId
 * @param {string} [args.type='Expense'] - 'Expense' (spend) or 'Income' (earnings)
 * @param {Date|string} args.periodStart
 * @param {Date|string} args.periodEnd
 * @param {Array} [args.previousPeriods] - [{start, end}] for history; derived
 *   by stepping the period back when omitted
 * @param {Array} args.transactions
 * @param {Array} [args.actions] - for committed occurrences (transfers and
 *   other-type actions are ignored)
 * @param {string} [args.method='auto'] - FORECAST_METHODS value; unknown
 *   values fall back to 'auto'
 * @param {Date} [args.today]
 * @returns {{
 *   forecast: number, spent: number, committed: number,
 *   committedItems: Array<{name: string, date: Date, amount: number}>,
 *   remainder: number, paceWeight: number,
 *   history: {amount: number, basis: 'median'|'same-month-last-year', periods: number}|null
 *     - for 'auto', amount is history's remainder estimate (spend after this
 *       point in prior periods); for 'typical', the whole-period median,
 *   method: 'auto'|'pace'|'typical', usedFallback: boolean,
 *   isLumpy: boolean, isPast: boolean,
 *   daysElapsed: number, daysRemaining: number, periodDays: number,
 * }|null}
 */
export const forecastCategory = ({
  categoryId,
  type = 'Expense',
  periodStart,
  periodEnd,
  previousPeriods,
  transactions = [],
  actions = [],
  method = DEFAULT_FORECAST_METHOD,
  today = new Date(),
} = {}) => {
  const forecastMethod = normalizeMethod(method);
  const start = toLocalDay(periodStart);
  const end = toLocalDay(periodEnd);
  const now = toLocalDay(today);
  if (!start || !end || end < start) return null;

  const wantPositive = type === 'Income';
  const inCategory = (t) =>
    (t.category || 'uncategorized') === (categoryId || 'uncategorized') &&
    (wantPositive ? t.amount > 0 : t.amount < 0);

  const sumSpend = (from, to) => {
    let total = 0;
    let count = 0;
    transactions.forEach((t) => {
      if (!inCategory(t)) return;
      const d = toLocalDay(t.date);
      if (!d || d < from || d > to) return;
      total += Math.abs(t.amount);
      count += 1;
    });
    return { total, count };
  };

  const periodDays = dayDiff(end, start) + 1;
  const spentTo = now < end ? now : end;
  const { total: spent } = spentTo >= start ? sumSpend(start, spentTo) : { total: 0 };

  const base = {
    spent: round2(spent),
    committed: 0,
    committedItems: [],
    remainder: 0,
    paceWeight: 0,
    history: null,
    method: forecastMethod,
    usedFallback: false,
    isLumpy: false,
    daysElapsed: Math.min(Math.max(dayDiff(now, start) + 1, 0), periodDays),
    daysRemaining: Math.max(dayDiff(end, now), 0),
    periodDays,
  };

  // Completed period: the landing figure is known.
  if (end <= now) {
    return { ...base, forecast: round2(spent), isPast: true };
  }

  // Committed: action occurrences of this category still to land this period.
  const actionType = wantPositive ? 'income' : 'expense';
  const committedItems = [];
  if (categoryId) {
    actions
      .filter((a) => a && a.isActive !== false && !a.isCompleted)
      .filter((a) => a.categoryId === categoryId && a.type === actionType)
      .forEach((a) => {
        const amount = Math.abs(Number(a.amount) || 0);
        if (!amount) return;
        generateOccurrences(a, addDays(now, 1), end).forEach((date) => {
          committedItems.push({ name: a.name, date, amount });
        });
      });
  }
  committedItems.sort((a, b) => a.date - b.date);
  const committed = committedItems.reduce((s, i) => s + i.amount, 0);

  // History: previous like-for-like periods, newest first. Periods from
  // before the transaction history began are unknowable, not $0. Each known
  // period carries two views: its whole-period total (what 'typical' uses)
  // and its tail — what landed after the fraction-of-period point today sits
  // at — which is history's estimate of this period's remainder. The tail is
  // what makes the blend day-aware: once the rent or a paycheck has landed,
  // prior periods' tails say "nothing more usually comes", while a
  // late-month bill still shows up in them.
  const daysElapsed = Math.min(Math.max(dayDiff(now, start) + 1, 0), periodDays);
  const daysRemaining = dayDiff(end, now);
  const fracElapsed = periodDays > 0 ? daysElapsed / periodDays : 1;

  const prior = previousPeriods || derivePreviousPeriods(start, end, SEASONAL_MIN_PERIODS);
  let earliestTxn = null;
  transactions.forEach((t) => {
    const d = toLocalDay(t.date);
    if (d && (!earliestTxn || d < earliestTxn)) earliestTxn = d;
  });
  const knownPeriods = prior
    .map((p) => ({ start: toLocalDay(p.start), end: toLocalDay(p.end) }))
    .filter((p) => p.start && p.end && earliestTxn && p.end >= earliestTxn && p.end < now)
    .map((p) => {
      const len = dayDiff(p.end, p.start) + 1;
      const tailStart = addDays(p.start, Math.min(Math.round(fracElapsed * len), len));
      const tail = tailStart <= p.end ? sumSpend(tailStart, p.end).total : 0;
      return { ...p, ...sumSpend(p.start, p.end), tail };
    });

  const recent = knownPeriods.slice(0, HISTORY_PERIODS);
  const recentTotals = recent.map((p) => p.total);
  let history = null;
  if (recentTotals.length) {
    // Same-month-last-year beats the recent median once a full year of
    // monthly history exists (December ≠ July).
    const seasonal =
      knownPeriods.length >= SEASONAL_MIN_PERIODS &&
      periodDays >= 28 && periodDays <= 31 &&
      knownPeriods[SEASONAL_MIN_PERIODS - 1];
    history = seasonal
      ? { amount: seasonal.tail, basis: 'same-month-last-year', periods: knownPeriods.length }
      : { amount: median(recent.map((p) => p.tail)), basis: 'median', periods: recent.length };
  }

  // Lumpy category: a handful of transactions per period means day-to-day
  // pace is noise, so history keeps more of the weight for longer.
  const counts = recent.map((p) => p.count);
  const isLumpy = counts.length > 0 && median(counts) <= LUMPY_TXN_COUNT;

  const paceRemainder = daysElapsed > 0 ? (spent / daysElapsed) * daysRemaining : 0;
  const paceForecast = spent + committed + paceRemainder;

  // Squared, not linear: early-period pace is mostly noise, so it earns
  // trust slowly and only takes over once most of the period is evidence.
  let paceWeight = fracElapsed * fracElapsed;
  if (isLumpy) paceWeight *= LUMPY_PACE_DAMPING;

  let forecast;
  let usedFallback = false;

  if (forecastMethod === 'pace') {
    // Current pace: this period's daily rate carried through the days left.
    forecast = paceForecast;
    paceWeight = 1;
  } else if (forecastMethod === 'typical') {
    // Median of the last completed periods (spike-resistant); the total
    // can't sit below what's already locked in. No completed periods yet →
    // only pace can speak.
    if (recentTotals.length) {
      const med = median(recentTotals);
      history = { amount: med, basis: 'median', periods: recentTotals.length };
      forecast = Math.max(med, spent + committed);
      paceWeight = 0;
    } else {
      history = null;
      forecast = paceForecast;
      paceWeight = 1;
      usedFallback = true;
    }
  } else if (history) {
    // 'auto': blend the two *remainder* estimates — history's tail (what
    // usually still lands from this point) with this period's pace. Spent
    // and committed are facts, not estimates: they sit outside the blend,
    // so the forecast can never fall below what's already locked in.
    forecast = spent + committed + paceWeight * paceRemainder + (1 - paceWeight) * history.amount;
  } else {
    forecast = paceForecast;
    paceWeight = 1;
  }

  return {
    ...base,
    forecast: round2(forecast),
    committed: round2(committed),
    committedItems,
    remainder: round2(Math.max(0, forecast - spent - committed)),
    paceWeight: round2(paceWeight),
    history: history ? { ...history, amount: round2(history.amount) } : null,
    usedFallback,
    isLumpy,
    isPast: false,
    daysElapsed,
    daysRemaining,
  };
};
